import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router";
import { ColumnsType } from "antd/lib/table";
import styled from "styled-components";
import Buttons from "../../components/atoms/Buttons";
import PageFilter from "../../components/molecures/PageFilter";
import ExpandableTable from "../../components/molecures/ExpandableTable";

import { HttpError } from "../../network/types";
import { alertError, changeIsoToKorDate } from "../../shared/utils";
import { getInquiryCategories, getInquiryList } from "../../apis/Inquiry/apis";
import { InquiryListItem } from "../../apis/Inquiry/apis/types";
import RegisterModal from "./components/RegisterModal";
import { theme } from "../../theme";
import StatusDiv from "../../components/molecures/StatusDiv";
import useModals from "../../hooks/useModals";

import useDidChangetEffect from "../../hooks/useDidChangeEffect";
import { Service } from "../../shared/enums";
import useShop from "../../hooks/useShop";
import { getDecodeToken } from "../../shared/utils/Token";
import useUser from "../../hooks/useUsers";

const Inquiry: React.FC = () => {
  const { pathname, search } = useLocation();
  const { openModal } = useModals();
  const nav = useNavigate();
  const searchParams = new URLSearchParams(search);
  const { shop } = useShop();
  const { getUserID } = useUser();

  const [rows, setRows] = useState<InquiryListItem[]>([]);
  const [entireId, setEntireID] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>(0);

  const getCategoriesMutation = useMutation(getInquiryCategories, {
    onSuccess: (res) => {
      if (res.rows.find((val) => val.categoryName === "전체")) {
        pushPage(page, pageSize, {
          inquiryCategoryId: res.rows.find((val) => val.categoryName === "전체")
            ?.id,
        });
        setEntireID(res.rows.find((val) => val.categoryName === "전체")?.id);
      }
      onSearch();
    },
    onError: (err: HttpError) => {
      if (err.status !== 404) alertError(err.message);
    },
  });
  const getPageListMutation = useMutation(getInquiryList, {
    onSuccess: (res) => {
      setRows(
        res.inquiryList
          ? res.inquiryList?.map((val) => ({
              ...val,
              category:
                val.inquiryCategory?.categoryName || val.category || "-",
            }))
          : []
      );
      setTotalCount(res.count || 0);
    },
    onError: (err: HttpError) => {
      if (err.status !== 404) alertError(err.message);
      setRows([]);
    },
  });
  const DEFAULT_PAGE = 1;
  const DEFAULT_PAGE_SIZE = 10;
  const QUERY_KEY_FILTER = "filter";
  const QUERY_KEY_PAGE = "page";
  const QUERY_KEY_PAGE_SIZE = "pageSize";
  const getPaginationInfoFromQuery = () => {
    try {
      const pageInQuery = searchParams.get(QUERY_KEY_PAGE) || `${DEFAULT_PAGE}`;
      const filterValues = JSON.parse(
        searchParams.get(QUERY_KEY_FILTER) ||
          JSON.stringify({
            inquiryCategoryId: entireId,
          })
      );
      const pageSizeInQuery =
        searchParams.get(QUERY_KEY_PAGE_SIZE) || `${DEFAULT_PAGE_SIZE}`;
      const page = parseInt(pageInQuery, 10);
      const pageSize = parseInt(pageSizeInQuery, 10);
      return {
        page,
        pageSize,
        filterValues,
      };
    } catch (e) {
      return {
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        filterValues: {},
      };
    }
  };
  const { page, pageSize } = getPaginationInfoFromQuery();

  const pushPage = (page: number, pageSize: number, filterValues = {}) => {
    const filterString = JSON.stringify(filterValues);

    const queryString = new URLSearchParams({
      page: `${page}`,
      pageSize: `${pageSize}`,
      [QUERY_KEY_FILTER]: filterString,
    }).toString();
    const nextUrl = `${pathname}?${queryString}`;
    nav(nextUrl);
  };

  const handlePaginationChange = (page: number) => {
    pushPage(page, pageSize);
  };
  const handlePageSizeChange = (nextPageSize: number) => {
    pushPage(1, nextPageSize);
  };

  const columns: ColumnsType = [
    {
      title: "등록일",
      dataIndex: "createdAt",
      width: 152,
      render: (value) => {
        return changeIsoToKorDate(value);
      },
    },
    {
      title: "문의유형",
      dataIndex: "category",
      width: 152,
    },
    {
      title: "답변여부",
      width: 152,
      dataIndex: "status",
      render: (value) => {
        return (
          <StatusDiv isExposed={value === "replied"}>
            {value === "replied"
              ? "답변 완료"
              : value === "received"
              ? "접수완료"
              : "답변 대기"}
          </StatusDiv>
        );
      },
    },
    {
      title: "제목",
      width: 760,
      dataIndex: "title",
      render: (value) => <StyledTitle>{value}</StyledTitle>,
    },
  ];

  const onSearch = () => {
    getPageListMutation.mutate({
      page:
        searchParams.get("page") === null
          ? 1
          : Number.isNaN(searchParams.get("page"))
          ? 1
          : Number(searchParams.get("page")),
      pageSize:
        searchParams.get("pageSize") === null
          ? 10
          : Number.isNaN(searchParams.get("pageSize"))
          ? 10
          : Number(searchParams.get("pageSize")),
      inquiryCategoryId:
        searchParams.get("inquiryCategoryId") === null
          ? 17
          : Number.isNaN(searchParams.get("inquiryCategoryId"))
          ? 17
          : Number(searchParams.get("inquiryCategoryId")),
    });
  };

  useEffect(() => {
    getCategoriesMutation.mutate({
      isUsedEvery: true,
      viewable: true,
      service: Service.REPAIR_SHOP_WEB,
    });
  }, []);

  useDidChangetEffect(() => {
    onSearch();
  }, [search]);

  useEffect(() => {
    if (isMaster) {
      nav("/faq");
    } else {
      onSearch();
    }
  }, [shop]);

  const isMaster = useMemo(() => {
    const info = getDecodeToken();
    return info?.isMaster && +shop === getUserID();
  }, [shop]);

  return (
    <>
      <PageFilter
        title="1:1 문의"
        smallPageTitle="고객센터"
        margin="0 0 24px 0px"
        inputs={[
          <Buttons
            key={`inquiry${1}`}
            size="medium"
            text="1:1 문의하기"
            onClick={() => {
              openModal(<RegisterModal refetchAction={onSearch} />);
            }}
          />,
        ]}
      />
      <ExpandableTable
        title="1:1 문의 내역"
        columns={columns}
        isGenerateBanned
        onPageChanged={handlePaginationChange}
        onPageSizeChanged={handlePageSizeChange}
        data={rows || []}
        totalCount={totalCount || 0}
        pageSize={pageSize}
        handleModal={undefined}
        currentPage={page}
        isLoading={getPageListMutation.isLoading}
      />
    </>
  );
};

export default Inquiry;
const StyledTitle = styled.span`
  ${theme.typo.heading.b14};
`;
